if (!customElements.get('fbt-popup')) {
  /**
   * Frequently-bought-together popup.
   *
   * The markup is produced by `sections/fbt-popup.liquid` and arrives *inside the
   * add-to-cart response*: `product-form.js` names this section in the `sections=` parameter
   * of its POST to /cart/add, so showing the popup costs no extra request. Rendering stays in
   * Liquid, which is what keeps prices in the shopper's own market currency and text in their
   * own locale without any client-side pricing logic.
   */
  class FbtPopup extends HTMLElement {
    static SECTION_ID = 'fbt-popup';
    static STORAGE_KEY = 'fbt-popup-shown';

    /**
     * Swaps in freshly rendered markup and opens the popup.
     * @param {string} html - Rendered section HTML from the cart/add response.
     */
    static show(html) {
      const host = document.querySelector('fbt-popup');
      if (!host || !html) return;

      // "At most once per visit", when the section setting asks for it.
      if (host.hasAttribute('data-once-per-visit')) {
        try {
          if (sessionStorage.getItem(FbtPopup.STORAGE_KEY)) return;
        } catch (e) {
          // Private mode and blocked site data throw on access. A popup is not worth
          // failing an add-to-cart over, so treat it as "not shown yet" and carry on.
        }
      }

      let fresh;
      try {
        fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('fbt-popup');
      } catch (e) {
        return;
      }
      if (!fresh) return;

      host.innerHTML = fresh.innerHTML;

      // The theme lazy-loads images: the markup carries `data-src`/`data-srcset` and
      // something has to copy them into `src`/`srcset`. The page-load observer never saw
      // these nodes, because they arrived after it ran, so nothing would.
      //
      // On EU this was masked rather than handled — `cart-drawer.js` calls initLazyImages
      // after every render, 700ms after each add, which happened to catch our images too.
      // HU's cart drawer is disabled, so that never fired and the popup showed no pictures
      // at all. `product-recommendations` swaps content in the same way and calls this for
      // the same reason.
      if (typeof window.initLazyImages === 'function') window.initLazyImages();

      // No <modal-dialog> means the section found nothing worth recommending — everything
      // paired with this basket is already in it, sold out, or simply not mapped. Staying
      // silent is the correct outcome, not an error.
      const modal = host.querySelector('modal-dialog');
      if (!modal) return;

      // The element is upgraded by the parser on insert, but `open` only exists once the
      // modal-dialog definition has loaded. Wait for it rather than throwing.
      customElements.whenDefined('modal-dialog').then(() => {
        if (typeof modal.open !== 'function') return;
        modal.open();
        try {
          sessionStorage.setItem(FbtPopup.STORAGE_KEY, '1');
        } catch (e) {
          // See above — storage being unavailable must not break the popup itself.
        }
      });
    }
  }

  customElements.define('fbt-popup', FbtPopup);
  window.FbtPopup = FbtPopup;
}
